import { Dispatch } from 'redux';
import { useCallback } from 'react';
import { useDispatch } from 'react-redux';

import { UnknownFunction } from '@eon-home/react-library';

import {
    ResponseError,
    ElectricCarApi,
    ScheduleChargingModel,
    GenericSmartChargingModel,
    ElectricCarResponseModelV1,
    GenericSmartChargingModelModeEnum,
    GoogleAPILocationRequestModel as ElectricCarAddress,
    ElectricCarChargingModeCommandModelV1ActionEnum as CommandsEnum,
    ElectricCarHistorySessionV2CalculatedResponseModel,
    AutopilotChargingModel,
    PvForecastChargingModel,
    EeaFlexChargingModel,
    FastChargeModel,
    GatewayApi,
    SurplusToleranceResponseModel,
    InnovationChargingModel,
    IoTAccountPairingApi,
    IotPairingLocationInformationModel,
    HistoricalDataApi,
} from '@swagger-http';

import { store } from '@src/index';
import { Scope } from '@tools/enums';
import {
    AggregatedDataUserState,
    ChargingModeModel,
    EmobilityAction,
} from '@store/types';
import { useElectricCarConfigId } from '@store/selectors';
import {
    setSuccessToast,
    handleHistoricalEmobilityData,
    handleHistoricalEmobilityAssetMonitoringData,
} from '@store/actions';
import {
    EmobilityActionTypes,
    HistoricalResolution,
    LiveDataActionTypes,
} from '@store/enums';
import {
    handleError,
    checkForScopes,
    createRequestConfiguration,
    getResolution,
    getComparisonInterval,
    getRequestDates,
    useHasAutopilotChargingWriteScope,
    useHasEeaFlexChargingWriteScope,
    useHasInnovationChargingWriteScope,
} from '@tools/utils';

const {
    Schedule,
    Autopilot,
    EeaFlex,
    Innovation,
    PvForecast,
    FastCharging,
    // SolarAssistedCharging,
    // GridFriendlyCharging,
    // DynamicPriceCharging,
    // HomePlugAndCharge,
} = GenericSmartChargingModelModeEnum;

export interface EmobilityAddressDataProps {
    configId: string;
    address: ElectricCarAddress;
    isAddressSet: boolean;
}

export const AUTOPILOT_DEFAULT_TARGET_SOC = 80;
export const AUTOPILOT_DEFAULT_DEPATURE_TIME = '0700';
export const EEA_FLEX_DEFAULT_TARGET_SOC = 80;
export const EEA_FLEX_DEFAULT_DEPATURE_TIME = '0700';
export const INNOVATION_DEFAULT_TARGET_SOC = 80;
export const INNOVATION_DEFAULT_DEPATURE_TIME = '0700';
export const DEFAULT_MINIMUM_SOC_IF_MERCEDES = 50;
export const PVFORECAST_DEFAULT_TARGET_SOC = 80;
export const PVFORECAST_DEFAULT_DEPATURE_TIME = '0700';
export const PVFORECAST_DEFAULT_THRESHOLD = 25;
export const PVFORECAST_DEFAULT_AZIMUTH_DEG = 180;

export const getElectricCarData =
    () =>
    (dispatch: Dispatch<any>): Promise<EmobilityAction | void> => {
        if (!checkForScopes([Scope.ENERGYDEVICES_ELECTRIC_CAR_READ])) {
            return Promise.resolve();
        }

        return new ElectricCarApi(createRequestConfiguration())
            .electricCarListElectricCars()
            .then((data: ElectricCarResponseModelV1[]) => {
                return dispatch({
                    type: EmobilityActionTypes.ELECTRIC_CAR_SET_DEVICE_DATA,
                    payload: {
                        electricCar: data[0],
                    },
                });
            })
            .catch(async (e: ResponseError) => {
                await handleError(
                    e,
                    'Error when getting the electric car data:',
                );

                dispatch({
                    type: EmobilityActionTypes.ELECTRIC_CAR_SET_ERROR,
                    payload: {
                        error: e?.response,
                    },
                });
            });
    };

const setEvStateLoading = (evStateLoading: boolean) => ({
    type: LiveDataActionTypes.ELECTRIC_CAR_TELEMETRIES,
    payload: {
        evStateLoading,
    },
});

const setEvCommandError = (error: boolean) => ({
    type: LiveDataActionTypes.ELECTRIC_CAR_TELEMETRIES,
    payload: {
        commandError: error,
    },
});

export const sendElectricCarCommand =
    (configId: string, command: CommandsEnum) =>
    (dispatch: Dispatch<any>): Promise<EmobilityAction | void> => {
        if (!checkForScopes([Scope.ENERGYDEVICES_ELECTRIC_CAR_WRITE])) {
            return Promise.resolve();
        }

        dispatch(setEvCommandError(false));
        dispatch({
            type: LiveDataActionTypes.ELECTRIC_CAR_TELEMETRIES,
            payload: {
                evStateLoading: true,
                lastCommand: command,
            },
        });

        return new ElectricCarApi(createRequestConfiguration())
            .electricCarSetChargingMode({
                configId,
                electricCarChargingModeCommandModelV1: {
                    action: command,
                },
            })
            .then((data) => {
                if (data.failureReason) {
                    dispatch(setEvCommandError(true));
                    dispatch(setEvStateLoading(false));
                    return;
                }
            })
            .catch(async (e: ResponseError) => {
                dispatch(setEvCommandError(true));
                dispatch(setEvStateLoading(false));

                await handleError(
                    e,
                    'Error when calling electricCarSetChargingMode:',
                );
            });
    };

export const setAddress =
    (addressData: EmobilityAddressDataProps, isMercedes: boolean = false) =>
    (dispatch: Dispatch): Promise<boolean | void> => {
        if (!checkForScopes([Scope.ENERGYDEVICES_ELECTRIC_CAR_WRITE])) {
            return Promise.resolve(false);
        }

        const hasAutopilotChargingScope = useHasAutopilotChargingWriteScope();
        const hasEeaFlexChargingScope = useHasEeaFlexChargingWriteScope();
        const hasInnovationChargingScope = useHasInnovationChargingWriteScope();

        const isFirstTimeAddressIsSet = !addressData.isAddressSet;

        return new ElectricCarApi(createRequestConfiguration())
            .electricCarSetAddress({
                configId: addressData.configId,
                googleAPILocationRequestModel: addressData.address,
            })
            .then(() => {
                dispatch({
                    type: EmobilityActionTypes.ELECTRIC_CAR_SET_ADDRESS,
                });

                // automatically activate autopilot mode if the address is set for the first time
                if (hasAutopilotChargingScope && isFirstTimeAddressIsSet) {
                    dispatch(
                        setElectricCarAutopilotMode(addressData.configId, {
                            active: true,
                            departureTime: AUTOPILOT_DEFAULT_DEPATURE_TIME,
                            targetSoc: AUTOPILOT_DEFAULT_TARGET_SOC,
                            minimumSoc: isMercedes
                                ? DEFAULT_MINIMUM_SOC_IF_MERCEDES
                                : undefined,
                        }),
                    );
                } else if (hasEeaFlexChargingScope && isFirstTimeAddressIsSet) {
                    dispatch(
                        setElectricCarEeaFlexMode(addressData.configId, {
                            active: true,
                            departureTime: EEA_FLEX_DEFAULT_DEPATURE_TIME,
                            targetSoc: EEA_FLEX_DEFAULT_TARGET_SOC,
                            minimumSoc: isMercedes
                                ? DEFAULT_MINIMUM_SOC_IF_MERCEDES
                                : undefined,
                        }),
                    );
                } else if (
                    hasInnovationChargingScope &&
                    isFirstTimeAddressIsSet
                ) {
                    dispatch(
                        setElectricCarInnovationMode(addressData.configId, {
                            active: true,
                            departureTime: INNOVATION_DEFAULT_DEPATURE_TIME,
                            targetSoc: INNOVATION_DEFAULT_TARGET_SOC,
                            minimumSoc: isMercedes
                                ? DEFAULT_MINIMUM_SOC_IF_MERCEDES
                                : undefined,
                        }),
                    );
                }

                return true;
            })
            .catch(async (e: ResponseError) => {
                // address does not have to be changed when backend returns error
                await handleError(e, 'Error when setting address:');

                return false;
            });
    };

export const setAddressBeforeLinking = async (
    addressData: IotPairingLocationInformationModel,
) => {
    if (!checkForScopes([Scope.ENERGYDEVICES_ELECTRIC_CAR_WRITE])) {
        return Promise.resolve(false);
    }

    try {
        const linkData = await new IoTAccountPairingApi(
            createRequestConfiguration(),
        ).ioTAccountPairingGetVendorConfigV2({
            vendor: 'gridx',
            iotPairingLocationInformationModel: addressData,
        });
        return linkData;
    } catch (e) {
        await handleError(e, 'Error when setting address before linking:');
        return false;
    }
};

export const getElectricCarChargingModesData =
    (configId: string, silent = true) =>
    (dispatch: Dispatch): Promise<void> => {
        if (!checkForScopes([Scope.EMOBILITY_SMART_CHARGING_READ])) {
            return Promise.resolve();
        }

        if (!silent) {
            dispatch(setElectricCarChargingModesLoading(true));
        }

        dispatch(setElectricCarChargingModesError(false));

        return new ElectricCarApi(createRequestConfiguration())
            .electricCarGetSmartChargingModes({ configId })
            .then((data: GenericSmartChargingModel[]) => {
                dispatch({
                    type: EmobilityActionTypes.ELECTRIC_CAR_SET_CHARGING_MODES_DATA,
                    payload: {
                        data,
                    },
                });
            })
            .catch(async (e) => {
                if (e.response.status !== 404) {
                    await handleError(e, `Error calling GetSmartChargingMode:`);

                    dispatch(setElectricCarChargingModesError(true));
                }
            })
            .finally(() => dispatch(setElectricCarChargingModesLoading(false)));
    };

export const setElectricCarChargingModesError =
    (error: boolean) => (dispatch: Dispatch) =>
        dispatch({
            type: EmobilityActionTypes.ELECTRIC_CAR_SET_CHARGING_MODES_ERROR,
            payload: {
                error,
            },
        });

export const setElectricCarChargingModesLoading =
    (loading: boolean) => (dispatch: Dispatch) =>
        dispatch({
            type: EmobilityActionTypes.ELECTRIC_CAR_SET_CHARGING_MODES_LOADING,
            payload: {
                loading,
            },
        });

export const setElectricCarChargingModesSyncing =
    (syncing: boolean) => (dispatch: Dispatch) =>
        dispatch({
            type: EmobilityActionTypes.ELECTRIC_CAR_SET_CHARGING_MODES_SYNCING,
            payload: {
                syncing,
            },
        });

export const setElectricCarChargingModeError =
    (mode: GenericSmartChargingModelModeEnum, error: ResponseError) =>
    (dispatch: Dispatch) => {
        let hasMatch = false;
        const data = store
            .getState()
            .emobility.electricCarData.chargingModes.data.map((item) => {
                if (!hasMatch) {
                    hasMatch = item.mode === mode;
                }

                return {
                    ...item,
                    error: hasMatch ? error : item.error,
                };
            });

        if (!hasMatch) {
            data.push({
                mode,
                error,
                parameters: { active: false },
            });
        }

        dispatch({
            type: EmobilityActionTypes.ELECTRIC_CAR_SET_CHARGING_MODE_ERROR,
            payload: {
                data,
            },
        });
    };

export const toggleElectricCarChargingModes =
    (mode: GenericSmartChargingModelModeEnum, isActive: boolean) =>
    (dispatch: Dispatch) => {
        const data = store
            .getState()
            .emobility.electricCarData.chargingModes.data.map((item) => ({
                ...item,
                parameters: {
                    ...item.parameters,
                    active: item.mode === mode ? isActive : false,
                },
            }));

        dispatch({
            type: EmobilityActionTypes.ELECTRIC_CAR_SET_CHARGING_MODES_DATA,
            payload: {
                data,
            },
        });
    };

export const onSuccessfulElectricCarChargingModeChange =
    (configId: string) => (dispatch: Dispatch) => {
        dispatch(setSuccessToast());
        dispatch(getElectricCarChargingModesData(configId));
    };

export const setElectricCarSmartSchedulingMode =
    (
        configId: string,
        scheduleChargingModel: ScheduleChargingModel,
        callback?: UnknownFunction,
    ) =>
    (dispatch: Dispatch) => {
        if (!checkForScopes([Scope.EMOBILITY_SMART_CHARGING_WRITE])) {
            return Promise.resolve();
        }

        dispatch(
            toggleElectricCarChargingModes(
                Schedule,
                scheduleChargingModel.active,
            ),
        );

        if (typeof callback === 'function') {
            callback();
        }

        return new ElectricCarApi(createRequestConfiguration())
            .electricCarSetSmartChargingModeToSchedule({
                configId,
                scheduleChargingModel,
                takeInstantEffect: 'true',
            })
            .then(() => {
                dispatch(onSuccessfulElectricCarChargingModeChange(configId));
            })
            .catch(async (e: ResponseError) => {
                await handleError(
                    e,
                    `Error calling SetSmartChargingModeToSchedule :`,
                );

                dispatch(setElectricCarChargingModeError(Schedule, e));
            });
    };

export const setElectricCarAutopilotMode =
    (
        configId: string,
        autopilotChargingModel: AutopilotChargingModel,
        callback?: UnknownFunction,
    ) =>
    (dispatch: Dispatch) => {
        if (!checkForScopes([Scope.EMOBILITY_SMART_CHARGING_WRITE])) {
            return Promise.resolve();
        }

        dispatch(setElectricCarChargingModesSyncing(true));

        dispatch(
            toggleElectricCarChargingModes(
                Autopilot,
                autopilotChargingModel.active,
            ),
        );

        if (typeof callback === 'function') {
            callback();
        }

        return new ElectricCarApi(createRequestConfiguration())
            .electricCarSetSmartChargingModeToAutopilot({
                configId,
                autopilotChargingModel,
                takeInstantEffect: 'true',
            })
            .then(() => {
                dispatch(onSuccessfulElectricCarChargingModeChange(configId));
            })
            .catch(async (e: ResponseError) => {
                await handleError(
                    e,
                    `Error calling SetSmartChargingModeToAutopilot:`,
                );

                dispatch(setElectricCarChargingModeError(Autopilot, e));
            })
            .finally(() => {
                dispatch(setElectricCarChargingModesSyncing(false));
            });
    };

const setElectricCarEeaFlexMode =
    (
        configId: string,
        eeaFlexChargingModel: EeaFlexChargingModel,
        callback?: UnknownFunction,
    ) =>
    (dispatch: Dispatch) => {
        if (!checkForScopes([Scope.EMOBILITY_SMART_CHARGING_WRITE])) {
            return Promise.resolve();
        }

        dispatch(
            toggleElectricCarChargingModes(
                EeaFlex,
                eeaFlexChargingModel.active,
            ),
        );

        if (typeof callback === 'function') {
            callback();
        }

        return new ElectricCarApi(createRequestConfiguration())
            .electricCarSetSmartChargingModeToEeaFlex({
                configId,
                eeaFlexChargingModel,
                takeInstantEffect: 'true',
            })
            .then(() => {
                dispatch(onSuccessfulElectricCarChargingModeChange(configId));
            })
            .catch(async (e: ResponseError) => {
                await handleError(
                    e,
                    `Error calling SetSmartChargingModeToEeaFlex:`,
                );

                dispatch(setElectricCarChargingModeError(EeaFlex, e));
            });
    };

const setElectricCarInnovationMode =
    (
        configId: string,
        innovationChargingModel: InnovationChargingModel,
        callback?: UnknownFunction,
    ) =>
    (dispatch: Dispatch) => {
        if (!checkForScopes([Scope.EMOBILITY_SMART_CHARGING_WRITE])) {
            return Promise.resolve();
        }

        dispatch(
            toggleElectricCarChargingModes(
                Innovation,
                innovationChargingModel.active,
            ),
        );

        if (typeof callback === 'function') {
            callback();
        }

        return new ElectricCarApi(createRequestConfiguration())
            .electricCarSetSmartChargingModeToInnovation({
                configId,
                innovationChargingModel,
                takeInstantEffect: 'true',
            })
            .then(() => {
                dispatch(onSuccessfulElectricCarChargingModeChange(configId));
            })
            .catch(async (e: ResponseError) => {
                await handleError(
                    e,
                    `Error calling SetSmartChargingModeToInnovation:`,
                );

                dispatch(setElectricCarChargingModeError(Innovation, e));
            });
    };

export const setElectricCarPvForecastMode =
    (
        configId: string,
        pvForecastChargingModel: PvForecastChargingModel,
        callback?: UnknownFunction,
    ) =>
    (dispatch: Dispatch) => {
        if (!checkForScopes([Scope.EMOBILITY_SMART_CHARGING_WRITE])) {
            return Promise.resolve();
        }

        dispatch(
            toggleElectricCarChargingModes(
                PvForecast,
                pvForecastChargingModel.active,
            ),
        );

        if (typeof callback === 'function') {
            callback();
        }

        return new ElectricCarApi(createRequestConfiguration())
            .electricCarSetSmartChargingModeToPvForecast({
                configId,
                pvForecastChargingModel,
                takeInstantEffect: 'true',
            })
            .then(() => {
                dispatch(onSuccessfulElectricCarChargingModeChange(configId));
            })
            .catch(async (e: ResponseError) => {
                await handleError(
                    e,
                    `Error calling SetSmartChargingModeToPvForecast:`,
                );

                dispatch(setElectricCarChargingModeError(PvForecast, e));
            });
    };

export const setElectricCarFastChargingMode =
    (
        configId: string,
        fastChargeModel: FastChargeModel,
        callback?: UnknownFunction,
    ) =>
    (dispatch: Dispatch) => {
        if (!checkForScopes([Scope.EV_FAST_CHARGING_C2C_WRITE])) {
            return Promise.resolve();
        }

        dispatch(setElectricCarChargingModesSyncing(true));

        dispatch(
            toggleElectricCarChargingModes(
                FastCharging,
                fastChargeModel.active,
            ),
        );

        if (typeof callback === 'function') {
            callback();
        }

        return new ElectricCarApi(createRequestConfiguration())
            .electricCarSetSmartChargingModeToFastCharging({
                configId,
                fastChargeModel,
            })
            .then(() => {
                dispatch(onSuccessfulElectricCarChargingModeChange(configId));
            })
            .catch(async (e: ResponseError) => {
                await handleError(
                    e,
                    `Error calling SetSmartChargingModeToFastCharging:`,
                );

                dispatch(setElectricCarChargingModeError(FastCharging, e));
            })
            .finally(() => {
                dispatch(setElectricCarChargingModesSyncing(false));
            });
    };

export const useSetElectricCarChargingMode = (
    mode: GenericSmartChargingModelModeEnum,
) => {
    const dispatch = useDispatch();
    const configId = useElectricCarConfigId();

    return useCallback(
        (parameters: ChargingModeModel, onComplete?: UnknownFunction): void => {
            if (!configId) {
                return;
            }

            switch (mode) {
                case Schedule:
                    //case GridFriendlyCharging:
                    dispatch(
                        setElectricCarSmartSchedulingMode(
                            configId,
                            parameters as ScheduleChargingModel,
                            onComplete,
                        ),
                    );
                    break;
                case Autopilot:
                    dispatch(
                        setElectricCarAutopilotMode(
                            configId,
                            parameters as AutopilotChargingModel,
                            onComplete,
                        ),
                    );
                    break;
                case EeaFlex:
                    dispatch(
                        setElectricCarEeaFlexMode(
                            configId,
                            parameters as EeaFlexChargingModel,
                            onComplete,
                        ),
                    );
                    break;
                case Innovation:
                    dispatch(
                        setElectricCarInnovationMode(
                            configId,
                            parameters as InnovationChargingModel,
                            onComplete,
                        ),
                    );
                    break;
                case PvForecast:
                    dispatch(
                        setElectricCarPvForecastMode(
                            configId,
                            parameters as PvForecastChargingModel,
                            onComplete,
                        ),
                    );
                    break;

                case FastCharging:
                    dispatch(
                        setElectricCarFastChargingMode(
                            configId,
                            parameters as FastChargeModel,
                            onComplete,
                        ),
                    );
                    break;

                /*
                case SolarAssistedCharging:
                    dispatch(
                        setSolarAssistedChargingMode(
                            configId,
                            parameters as SolarAssistedChargingModel,
                            onComplete,
                        ),
                    );
                    break;
                case DynamicPriceCharging:
                    dispatch(
                        setDynamicPriceChargingMode(
                            configId,
                            parameters as DynamicPriceChargingModel,
                            onComplete,
                        ),
                    );
                    break;
                case HomePlugAndCharge:
                    dispatch(
                        setPlugAndChargeMode(
                            configId,
                            parameters as PlugAndChargeModel,
                        ),
                    );*/
            }
        },
        [mode, configId, dispatch],
    );
};

export const getHistoricalElectricCarData = async (
    date: Date,
    res: HistoricalResolution,
    userState: AggregatedDataUserState,
    dispatch: Dispatch,
) => {
    if (!checkForScopes([Scope.ENERGYDEVICES_ELECTRIC_CAR_READ])) {
        return Promise.resolve({ prevDay: {} });
    }

    let error = false;

    const emobilityAPI = new HistoricalDataApi(createRequestConfiguration());
    const resolution = getResolution(res);
    const comparisonInterval = getComparisonInterval(res);
    const { startDate, endDate } = getRequestDates(date, res, false);

    const data = await emobilityAPI
        .historicalDataGetElectricCarHistoryV2({
            fromDate: startDate,
            toDate: endDate,
            interval: resolution,
            comparisonInterval,
        })
        .catch(async (e: ResponseError) => {
            await handleError(e, 'Error getting electric car historical data:');

            error = true;

            return [];
        });

    return handleHistoricalEmobilityData(
        date,
        res,
        error,
        dispatch,
        data,
        userState,
    );
};

export const getHistoricalAssetMonitoringData = async (
    date: Date,
    res: HistoricalResolution,
    userState: AggregatedDataUserState,
    dispatch: Dispatch,
) => {
    let error = false;

    const emobilityAPI = new HistoricalDataApi(createRequestConfiguration());
    const resolution = getResolution(res);
    const comparisonInterval = getComparisonInterval(res);
    const { startDate, endDate } = getRequestDates(date, res, false);

    const data = await emobilityAPI
        .historicalDataGetConsumptionHistoryAggregated({
            deviceType: 'EV',
            fromDate: startDate,
            toDate: endDate,
            interval: resolution,
            comparisonInterval,
        })
        .catch(async (e: ResponseError) => {
            await handleError(e, 'Error getting electric car historical data:');

            error = true;

            return [];
        });

    return handleHistoricalEmobilityAssetMonitoringData(
        date,
        res,
        error,
        dispatch,
        data,
        userState,
    );
};

export const getElectricCarChargingHistory =
    (fromDate: string, toDate: string) =>
    (dispatch: Dispatch): Promise<any> => {
        if (!checkForScopes([Scope.ENERGYDEVICES_ELECTRIC_CAR_READ])) {
            return Promise.resolve();
        }

        dispatch({
            type: EmobilityActionTypes.ELECTRIC_CAR_GET_CHARGING_HISTORY,
            payload: {
                error: false,
                loading: true,
            },
        });

        return new HistoricalDataApi(createRequestConfiguration())
            .historicalDataGetElectricCarHistorySessionsV2({
                fromDate,
                toDate,
            })
            .then(
                (
                    data: ElectricCarHistorySessionV2CalculatedResponseModel[],
                ) => {
                    dispatch({
                        type: EmobilityActionTypes.ELECTRIC_CAR_GET_CHARGING_HISTORY,
                        payload: { data },
                    });
                },
            )
            .catch(async (e: ResponseError) => {
                await handleError(
                    e,
                    'Error getting electric car charging history:',
                );

                dispatch({
                    type: EmobilityActionTypes.ELECTRIC_CAR_GET_CHARGING_HISTORY_ERROR,
                    payload: {
                        error: true,
                    },
                });
            })
            .finally(() => {
                dispatch({
                    type: EmobilityActionTypes.ELECTRIC_CAR_GET_CHARGING_HISTORY_LOADING,
                    payload: {
                        loading: false,
                    },
                });
            });
    };

// Charing surplus tolerance
export const getElectricCarChargingSurplusTolerance =
    (installationId: string) =>
    (dispatch: Dispatch): Promise<number | null> => {
        return new GatewayApi(createRequestConfiguration())
            .gatewayGetSurplusTolerance({
                installationId,
            })
            .then((data: SurplusToleranceResponseModel) => {
                dispatch({
                    type: EmobilityActionTypes.ELECTRIC_CAR_SET_CHARGING_SURPLUS_TOLERANCE,
                    payload: {
                        chargingSurplusTolerance: data.surplusTolerance ?? null,
                    },
                });

                return Promise.resolve(data.surplusTolerance ?? null);
            })
            .catch(async (e: ResponseError) => {
                await handleError(
                    e,
                    'Error getting charging surplus tolerance: ',
                );
                dispatch({
                    type: EmobilityActionTypes.ELECTRIC_CAR_SET_CHARGING_SURPLUS_TOLERANCE,
                    payload: { chargingSurplusTolerance: null },
                });

                return Promise.reject(null);
            });
    };

export const setElectricCarChargingSurplusTolerance =
    (installationId: string, surplusTolerance: number) =>
    (dispatch: Dispatch): Promise<number | null> => {
        return new GatewayApi(createRequestConfiguration())
            .gatewaySetSurplusTolerance({
                installationId,
                gatewaySetSurplusToleranceRequest: {
                    value: surplusTolerance,
                },
            })
            .then((data: SurplusToleranceResponseModel) => {
                dispatch({
                    type: EmobilityActionTypes.ELECTRIC_CAR_SET_CHARGING_SURPLUS_TOLERANCE,
                    payload: {
                        chargingSurplusTolerance: data.surplusTolerance ?? null,
                    },
                });
                dispatch(setSuccessToast());
                return Promise.resolve(data.surplusTolerance ?? null);
            })
            .catch(async (e: ResponseError) => {
                await handleError(
                    e,
                    'Error setting charging surplus tolerance: ',
                );
                return Promise.reject(e);
            });
    };

export const getRelinkDataForEnodeViaGridx = async () => {
    try {
        return await new IoTAccountPairingApi(
            createRequestConfiguration(),
        ).ioTAccountPairingRelinkVendor({ vendor: 'gridx' });
    } catch (e) {
        await handleError(
            e as ResponseError,
            'Error getting Relink Vendor data for Gridx: ',
        );
        return Promise.reject(e);
    }
};
